import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSelfHostedCameraController } from '../../services/SelfHostedCameraController';
import { createSelfHostedCameraTransport } from '../../services/selfHostedCameraTransport';
import { createSelfHostedCameraApi } from '../../services/selfHostedCameraApi';
import { cameraDeviceIdentity } from '../../services/cameraDeviceIdentity';
import {
  isInstalledAndroid, cameraPermissionPlugin, ensureNativeCameraPermission,
  readNativeCameraPermission, openNativeAppSettings,
} from '../../services/nativeCameraPermission';

const defaultNativeShell = () => typeof window !== 'undefined' && Boolean(window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
const settledLocally = () => Promise.resolve({ localReleased: true, owed: 0, confirmed: true, timedOut: false });

// Owns one controller per (login identity, synced backend owner) above the
// panel's lifetime. Mounting, opening the panel, settings reads, recovery and
// foreground return never capture: only an explicit Start or Preview does.
// Every Private Start is this device's video in the owner's daily Live Media.
export function useSelfHostedCamera({ backendService, identity, ownerId, loginSessionId, mediaOrigin, deps = {} }) {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const android = (deps.installedAndroid || isInstalledAndroid)();
  const [controller, setController] = useState(null);
  const [generation, setGeneration] = useState(0);
  const scope = useMemo(() => ({ identity, ownerId, backendService, loginSessionId, mediaOrigin, generation }),
    [identity, ownerId, backendService, loginSessionId, mediaOrigin, generation]);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const controllerScopeRef = useRef(null);
  // The scope whose controller construction threw; only a new scope retries.
  const [failedScope, setFailedScope] = useState(null);
  const [state, setState] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [permission, setPermission] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [audioOptIn, setAudioOptIn] = useState(false);
  const [cameras, setCameras] = useState(null);
  const [cameraId, setCameraId] = useState('');
  const [stopping, setStopping] = useState(null);
  const [startPending, setStartPending] = useState(null);
  const startBusyRef = useRef(null);
  const clearActionAdmission = useCallback(() => {
    // Invalidation releases admission, not a pending dependency. Every late
    // result is still fenced by actionRef and the initiating owner scope.
    startBusyRef.current = null; setStartPending(null);
  }, []);
  const verifyForAction = useCallback(async (c, isCurrent) => {
    const tracer = c.getState().settings?.privateTracer;
    if (!tracer) return true;
    if (!tracer.allowed) throw new Error('Private camera is unavailable for this account.');
    const result = await c.ownerApi.ownerEligibility();
    if (!isCurrent()) return false;
    if (result.cameraEligibility === 'ready') return true;
    throw new Error('Camera verification unavailable.');
  }, []);
  const controllerRef = useRef(null);
  const settlementRef = useRef(null);
  const restoreRef = useRef(null);
  const actionRef = useRef(0);
  const admittedRef = useRef(-1);
  const backgroundRef = useRef(false);
  const recoveredRef = useRef(null);
  const permissionRef = useRef(null);
  permissionRef.current = permission;
  const plugin = () => (depsRef.current.permissionPlugin || cameraPermissionPlugin)();
  const mediaDevices = () => depsRef.current.mediaDevices || (typeof navigator === 'undefined' ? null : navigator.mediaDevices);
  // Owner-scoped stable device identity; the display name is never identity.
  const liveDevice = () => (depsRef.current.deviceIdentity || cameraDeviceIdentity)(scopeRef.current.ownerId);
  // Device listing reads labels only; it never opens a camera.
  const listCameras = useCallback(c => {
    const devices = mediaDevices();
    if (typeof devices?.enumerateDevices !== 'function') return;
    Promise.resolve().then(() => devices.enumerateDevices()).then(list => {
      if (controllerRef.current !== c || !Array.isArray(list)) return;
      const video = list.filter(d => d?.kind === 'videoinput' && typeof d.deviceId === 'string' && d.deviceId);
      setCameras(video.map((d, i) => ({ id: d.deviceId, label: (typeof d.label === 'string' && d.label.trim()) || `Camera ${i + 1}` })));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const d = depsRef.current;
    setPermission(null); setActionError(null); setAudioOptIn(false); setCameras(null); setCameraId(''); setStopping(null);
    if (!identity || !ownerId || !mediaOrigin || backendService?.authenticatedIdentity !== identity
      || backendService?.positionOwner !== ownerId) {
      controllerRef.current = null; setController(null); setState(null);
      return undefined;
    }
    let c;
    try {
      const api = (d.createApi || createSelfHostedCameraApi)({ backendService, identity, ownerId });
      const onAndroid = (d.installedAndroid || isInstalledAndroid)();
      const options = {
        api, authorizedMediaOrigin: mediaOrigin, createOperationKey: d.createOperationKey,
        mediaDevices: d.mediaDevices, cleanupLedger: d.cleanupLedger, transportFactory: d.transportFactory,
        ...(d.timing || {}),
        // Installed Android uses standard WebView getUserMedia only after this
        // exact explicit action's OS camera grant. Any other native shell, or an
        // action without that grant, keeps the controller's unsupported rejection.
        isNativePlatform: () => (onAndroid || defaultNativeShell())
          && !(onAndroid && admittedRef.current === actionRef.current),
      };
      c = createSelfHostedCameraController(Object.fromEntries(Object.entries(options).filter(([, v]) => v !== undefined)));
      c.ownerApi = api;
      c.returnViewer = { mediaOrigin, getGrant: id => api.viewerToken(id),
        transportFactory: d.transportFactory || createSelfHostedCameraTransport,
        assertCurrent: () => { api.assertCurrent(); if (controllerRef.current !== c) throw new Error('Camera owner changed'); } };
    } catch {
      controllerRef.current = null; setController(null); setState(null); setFailedScope(scope);
      return undefined;
    }
    actionRef.current++; clearActionAdmission();
    controllerRef.current = c; controllerScopeRef.current = scope; recoveredRef.current = null;
    settlementRef.current = null; restoreRef.current = null;
    setController(c); setState(c.getState()); setPermission(null); setActionError(null); setAudioOptIn(false);
    const unsubscribe = c.subscribe(setState);
    const ownedActionRef = actionRef;
    return () => {
      // Account switch / unmount: invalidate pending grants, release local capture
      // synchronously; durable cleanup responsibility stays in the ledger.
      ownedActionRef.current++; clearActionAdmission();
      unsubscribe();
      if (controllerRef.current === c) controllerRef.current = null;
      void c.settleForSignOut();
    };
  }, [identity, ownerId, loginSessionId, mediaOrigin, backendService, generation, scope, clearActionAdmission]);

  const recheckPermission = useCallback(async () => {
    const outcome = permissionRef.current?.outcome;
    if (!android || !['denied', 'blocked', 'unavailable'].includes(outcome)) return;
    const c = controllerRef.current;
    const state = await readNativeCameraPermission({ plugin: plugin() });
    if (controllerRef.current !== c || permissionRef.current?.outcome !== outcome) return;
    // Settings return only refreshes guidance; a new explicit Preview is required.
    setPermission(state === 'granted' ? { outcome: 'allowed-after-settings' } : permissionRef.current);
  }, [android]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const background = () => {
      backgroundRef.current = true;
      actionRef.current++; clearActionAdmission();
      setPermission(p => (p?.outcome === 'checking' ? null : p));
      const c = controllerRef.current;
      if (c) void c.handleAppBackground();
    };
    const foreground = () => {
      if (!backgroundRef.current) return;
      backgroundRef.current = false;
      controllerRef.current?.handleAppForeground();
      void recheckPermission();
    };
    const visibility = () => (document.visibilityState === 'hidden' ? background() : foreground());
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', background);
    window.addEventListener('pageshow', foreground);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', background);
      window.removeEventListener('pageshow', foreground);
    };
  }, [recheckPermission, clearActionAdmission]);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
    const c = controllerRef.current;
    if (!c) return;
    c.attachPreviewSurface();
    if (recoveredRef.current !== c) {
      recoveredRef.current = c;
      // Reads only: saved defaults and earlier unconfirmed cleanup disclosure.
      c.refresh().catch(() => {});
      c.recover().catch(() => {});
    }
    listCameras(c);
  }, [listCameras]);

  const closePanel = useCallback(() => {
    actionRef.current++; clearActionAdmission();
    setPanelOpen(false); setPermission(null); setActionError(null); setAudioOptIn(false);
    const c = controllerRef.current;
    // Preview-only capture is cancelled; creating/connecting/live keeps running hidden.
    if (c) void c.detachPreviewSurface();
  }, [clearActionAdmission]);

  const preview = useCallback(async (live = null) => {
    const c = controllerRef.current;
    if (!c) return;
    const initiatingScope = scopeRef.current;
    const token = ++actionRef.current;
    const isCurrent = () => actionRef.current === token && controllerRef.current === c && scopeRef.current === initiatingScope && !backgroundRef.current;
    setActionError(null); setPermission(null);
    try {
      if (!c.getState().settings) await c.refresh();
      if (!isCurrent() || !await verifyForAction(c, isCurrent) || !isCurrent()) return;
    } catch (error) { if (isCurrent()) setActionError(error?.message || 'Camera verification unavailable.'); return; }
    if (android) {
      setPermission({ outcome: 'checking' });
      const outcome = await ensureNativeCameraPermission({ plugin: plugin(), isCurrent });
      if (!isCurrent()) return;
      if (outcome !== 'granted') { setPermission({ outcome }); return; }
      setPermission(null);
      admittedRef.current = token;
    }
    try {
      // The same explicit action continues after a grant; no retry is required.
      const settings = c.getState().settings;
      const tracer = settings?.privateTracer;
      if (tracer && !tracer.allowed) throw new Error('Private camera is unavailable for this account.');
      await c.openPreview({ audioEnabled: android || !settings || tracer ? false : audioOptIn, ...(cameraId ? { deviceId: cameraId } : {}) });
      if (isCurrent() && c.getState().status === 'preview') listCameras(c);
      if (live && isCurrent() && c.getState().status === 'preview') {
        await c.start({ audiences: [], live });
        if (controllerRef.current === c && scopeRef.current === initiatingScope) {
          window.dispatchEvent(new CustomEvent('camera-media-changed', { detail: { identity: initiatingScope.identity, ownerId: initiatingScope.ownerId } }));
        }
      }
    } catch (error) {
      if (isCurrent()) setActionError(error?.message || 'Camera could not start.');
    }
  }, [android, audioOptIn, cameraId, verifyForAction, listCameras]); // eslint-disable-line react-hooks/exhaustive-deps

  // The whole daily Live action: permission, capture and publication continue
  // from this one tap. A double tap or retained stale handler cannot start twice.
  const startLive = useCallback(async () => {
    const c = controllerRef.current;
    if (!c || startBusyRef.current) return;
    const attempt = { owner: scopeRef.current, controller: c };
    startBusyRef.current = attempt; setStartPending(attempt);
    try {
      let device;
      try { device = liveDevice(); } catch { setActionError('Camera could not start on this device.'); return; }
      await preview(device);
    } finally {
      // Only this exact attempt may release its lock.
      if (startBusyRef.current === attempt) startBusyRef.current = null;
      setStartPending(p => (p === attempt ? null : p));
    }
  }, [preview]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = useCallback(() => {
    actionRef.current++; clearActionAdmission();
    setPermission(null); setAudioOptIn(false);
    const c = controllerRef.current;
    if (c) void c.stop();
  }, [clearActionAdmission]);

  const start = useCallback(async audiences => {
    const c = controllerRef.current;
    if (!c) return;
    const token = actionRef.current; const initiatingScope = scopeRef.current;
    const isCurrent = () => actionRef.current === token && controllerRef.current === c && scopeRef.current === initiatingScope && !backgroundRef.current;
    setActionError(null);
    try {
      if (await verifyForAction(c, isCurrent) && isCurrent()) {
        // Shared audiences stay a separate broadcast; Private joins today's Live Media.
        await c.start({ audiences, ...(Array.isArray(audiences) && !audiences.length ? { live: liveDevice() } : {}) });
      }
    } catch (error) { if (isCurrent()) setActionError(error?.message || 'Camera could not start.'); }
  }, [verifyForAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = useCallback(() => {
    actionRef.current++; clearActionAdmission();
    setAudioOptIn(false);
    const c = controllerRef.current;
    if (!c) return Promise.resolve(null);
    // Local capture is released at once; Stopping… lasts until exact cleanup settles.
    const mark = { controller: c };
    setStopping(mark);
    return c.stop().finally(() => setStopping(s => (s === mark ? null : s)));
  }, [clearActionAdmission]);

  const retryStop = useCallback(async () => {
    const c = controllerRef.current;
    if (!c) return null;
    const result = await c.retryStop();
    if (restoreRef.current?.controller === c && controllerRef.current === c) {
      const settlement = await c.settleForSignOut();
      settlementRef.current = { controller: c, result: settlement };
      restoreRef.current?.resume();
    }
    return result;
  }, []);

  const settleForSignOut = useCallback(() => {
    const c = controller;
    if (controllerRef.current === c) { actionRef.current++; clearActionAdmission(); }
    if (!c) return settledLocally();
    return c.settleForSignOut().then(result => {
      if (controllerRef.current === c) settlementRef.current = { controller: c, result };
      return result;
    });
  }, [controller, clearActionAdmission]);

  // A rejected Clerk logout leaves the disposed instance permanently inert.
  // Replace it only if this exact controller still belongs to the same owner.
  const restoreAfterFailedSignOut = useCallback(() => {
    const scope = scopeRef.current;
    if (!controller || controllerRef.current !== controller || scope.identity !== identity
      || scope.ownerId !== ownerId || scope.loginSessionId !== loginSessionId || scope.backendService !== backendService
      || backendService?.authenticatedIdentity !== identity || backendService?.positionOwner !== ownerId) return;
    if (settlementRef.current?.controller !== controller || !settlementRef.current.result.confirmed) {
      restoreRef.current = { controller, resume: restoreAfterFailedSignOut };
      setActionError('Sign out failed. Camera cleanup is still unconfirmed. Retry stop before using the camera again.');
      return;
    }
    restoreRef.current = null;
    actionRef.current++; clearActionAdmission();
    setPanelOpen(false);
    setGeneration(value => value + 1);
    // The successor uses the same durable ledger. Opening it explicitly reads
    // pending debt; restoration never captures or resumes a broadcast.
  }, [controller, identity, ownerId, loginSessionId, backendService, clearActionAdmission]);

  const openAppSettings = useCallback(() => openNativeAppSettings({ plugin: plugin() }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const current = controllerScopeRef.current === scope;
  const ready = current && Boolean(controller);
  // Why capture is unavailable: setup missing (no media origin), the account
  // still syncing, or this scope's controller failed to initialize.
  const availability = ready ? 'ready' : !mediaOrigin ? 'unconfigured' : failedScope === scope ? 'failed' : 'connecting';
  return {
    startLive, stopping: current && stopping?.controller === controller,
    startPending: current && startPending?.controller === controller && startPending.owner === scope,
    cameras: current && cameras?.length > 1 ? cameras : null, cameraId, setCameraId: value => setCameraId(typeof value === 'string' ? value : ''),
    ownerGeneration: scope, returnViewer: current ? controller?.returnViewer : null,
    ready, availability, configured: Boolean(mediaOrigin), android,
    state: (current && state) || { status: 'off', previewStream: null, cleanupPending: false, error: null, settings: null },
    panelOpen, permission: current ? permission : null, actionError: current ? actionError : null, audioOptIn: current && audioOptIn,
    setAudioOptIn: value => { if (!android) setAudioOptIn(value === true); },
    openPanel, closePanel, preview, cancel, start, stop, retryStop, settleForSignOut, restoreAfterFailedSignOut, openAppSettings,
  };
}
